// ===================================================================
// src/app/contexts/ThemeContext.tsx
//
// Tema claro/escuro global do app (padrão Notion/Linear/Supabase/Trello):
// três modos (light/dark/system), persistido em localStorage, aplicado
// via classe `.dark` no <html> — os tokens de cor já existem prontos em
// src/styles/theme.css (@custom-variant dark (&:is(.dark *))), só nunca
// tinham sido ligados globalmente. Sem flash: index.html tem um script
// inline que aplica a classe antes do primeiro paint, lendo a mesma
// chave de localStorage usada aqui.
// ===================================================================

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'sophia_theme';

/**
 * Tema escuro desligado por decisão de produto (0013, 29/09/2026): só ~66 de
 * ~298 telas têm variante `dark:`, e quem usa o SO em modo escuro caía em telas
 * quebradas (texto claro sobre fundo claro fixo, ex.: títulos da Triagem).
 * Enquanto `false`, o app é sempre claro e o seletor de tema some — a
 * preferência salva em localStorage NÃO é apagada, volta a valer quando a
 * migração das telas terminar e isto voltar a `true`.
 * Manter em sincronia com o script inline de `index.html`.
 */
export const DARK_THEME_ENABLED = false;

interface ThemeContextValue {
  /** Preferência escolhida pelo usuário — pode ser 'system'. */
  mode: ThemeMode;
  /** Tema efetivamente aplicado ('system' já resolvido para light/dark). */
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Alterna entre claro e escuro (sai de 'system' ao usar isso). */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

function readStoredMode(): ThemeMode {
  if (!DARK_THEME_ENABLED) return 'light';
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;
}

function applyResolvedTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(readStoredMode()));

  const setMode = useCallback((next: ThemeMode) => {
    if (!DARK_THEME_ENABLED) return;
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // localStorage indisponível (modo privado/quota) — tema só não persiste
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setMode(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setMode]);

  // Aplica sempre que o modo muda
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
  }, [mode]);

  // Em modo 'system', acompanha mudança de preferência do SO em tempo real
  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const resolved: ResolvedTheme = mq.matches ? 'dark' : 'light';
      setResolvedTheme(resolved);
      applyResolvedTheme(resolved);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme deve ser usado dentro de <ThemeProvider>');
  return ctx;
}
